/**
 * Auth Actions
 *
 * Actions asincrone per autenticazione utente con Laravel Sanctum
 */

import {
  login as loginAPI,
  register as registerAPI,
  logout as logoutAPI,
  getAuthUser,
  updateUserProfile as updateUserProfileAPI,
  mergeGuestCart as mergeGuestCartAPI,
} from '../../api/backend';

import {
  AUTH_LOGIN_REQUEST,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGIN_FAILURE,
  AUTH_REGISTER_REQUEST,
  AUTH_REGISTER_SUCCESS,
  AUTH_REGISTER_FAILURE,
  AUTH_LOGOUT,
  AUTH_LOAD_USER_SUCCESS,
  AUTH_LOAD_USER_FAILURE,
  AUTH_UPDATE_PROFILE_SUCCESS,
  AUTH_CLEAR_ERROR,
} from '../reducers/auth';

import { getSessionId, clearSession } from '../../utils/session';
import { loadCart } from './cartActions';

// ==================== LOGIN ====================

export const login = (email, password) => async (dispatch) => {
  try {
    dispatch({ type: AUTH_LOGIN_REQUEST });

    const response = await loginAPI(email, password);
    const { user, token } = response;

    // Salva token e user in localStorage
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));

    dispatch({ type: AUTH_LOGIN_SUCCESS, payload: { user, token } });

    // Auto-merge carrello guest se presente
    const sessionId = getSessionId();
    if (sessionId) {
      try {
        await mergeGuestCartAPI(sessionId);
        clearSession();
      } catch (err) {
        // Non bloccare il login se il merge fallisce
        console.warn('Cart merge failed:', err);
      }
    }

    // Ricarica carrello utente
    dispatch(loadCart());

    return response;
  } catch (error) {
    const status = error?.response?.status || error?.status;
    const raw = error?.email?.[0] || error?.message || '';

    let message;
    if (status === 404 || status === 422 || /not found|doesn.*exist/i.test(raw)) {
      message = 'Nessun account trovato con questa email. Registrati per creare un account.';
    } else if (status === 401 || /invalid|credentials|unauthorized/i.test(raw)) {
      message = 'Email o password non corretti. Riprova.';
    } else if (status >= 500 || /SQLSTATE|exception|table|query/i.test(raw)) {
      message = 'Servizio temporaneamente non disponibile. Riprova tra qualche minuto.';
    } else {
      message = 'Credenziali non valide. Se non hai un account, registrati prima.';
    }

    dispatch({ type: AUTH_LOGIN_FAILURE, payload: message });
    throw error;
  }
};

// ==================== REGISTER ====================

export const register = (userData) => async (dispatch) => {
  try {
    dispatch({ type: AUTH_REGISTER_REQUEST });

    const response = await registerAPI(userData);
    const { user, token } = response;

    // Salva token e user in localStorage
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));

    dispatch({ type: AUTH_REGISTER_SUCCESS, payload: { user, token } });

    // Auto-merge carrello guest se presente
    const sessionId = getSessionId();
    if (sessionId) {
      try {
        await mergeGuestCartAPI(sessionId);
        clearSession();
      } catch (err) {
        console.warn('Cart merge failed:', err);
      }
    }

    // Ricarica carrello utente
    dispatch(loadCart());

    return response;
  } catch (error) {
    const status = error?.response?.status || error?.status;
    const raw = error?.email?.[0] || error?.name?.[0] || error?.message || '';

    let message;
    if (error?.email?.[0]) {
      message = error.email[0];
    } else if (error?.name?.[0]) {
      message = error.name[0];
    } else if (status >= 500 || /SQLSTATE|exception|table|query/i.test(raw)) {
      message = 'Servizio temporaneamente non disponibile. Riprova tra qualche minuto.';
    } else {
      message = raw || 'Errore durante la registrazione. Riprova.';
    }

    dispatch({ type: AUTH_REGISTER_FAILURE, payload: message });
    throw error;
  }
};

// ==================== LOGOUT ====================

export const logout = () => async (dispatch) => {
  try {
    await logoutAPI();
  } catch (err) {
    // Procedi comunque con il logout locale
    console.warn('Logout API error:', err);
  } finally {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    dispatch({ type: AUTH_LOGOUT });
  }
};

// ==================== LOAD USER (da token salvato) ====================

export const loadUser = () => async (dispatch) => {
  const token = localStorage.getItem('auth_token');
  if (!token) return;

  try {
    const user = await getAuthUser();
    // Aggiorna localStorage con dati freschi
    localStorage.setItem('auth_user', JSON.stringify(user));
    dispatch({ type: AUTH_LOAD_USER_SUCCESS, payload: user });
  } catch (err) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    dispatch({ type: AUTH_LOAD_USER_FAILURE });
  }
};

// ==================== UPDATE PROFILE ====================

export const updateProfile = (userData) => async (dispatch) => {
  try {
    const updatedUser = await updateUserProfileAPI(userData);
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    dispatch({ type: AUTH_UPDATE_PROFILE_SUCCESS, payload: updatedUser });
    return updatedUser;
  } catch (error) {
    throw error;
  }
};

// ==================== CLEAR ERROR ====================

export const clearAuthError = () => ({ type: AUTH_CLEAR_ERROR });
